/**
 * Heatmap Import Service
 * API-anrop för CSV-import och temporär heatmap-data
 */

import api from '../api/client';
import Cookies from 'js-cookie';

export interface ImportResult {
  sessionId: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  vehicles: string[];
  errors?: string[];
}

export interface ImportMetadata {
  userId: string;
  sessionId: string;
  importedAt: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  vehicles: string[];
  dateRange: {
    from: string;
    to: string;
  };
}

class HeatmapImportService {
  /**
   * Upload CSV-fil
   */
  async uploadCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    // Debug logging
    console.log('🔵 HeatmapImport: Starting upload', {
      fileName: file.name,
      fileSize: file.size,
      hasAccessToken: !!document.cookie.includes('accessToken'),
      cookies: document.cookie,
    });

    try {
      // VIKTIGT: Sätt INTE Content-Type manuellt för FormData!
      // Axios sätter det automatiskt med korrekt boundary
      console.log('🔵 HeatmapImport: Making API call', {
        url: '/api/heatmap-import/upload',
        baseURL: (api as any).defaults?.baseURL || 'default',
        hasFormData: formData instanceof FormData,
        fileSize: file.size,
      });

      const response = await api.post<{ success: boolean; data: ImportResult }>(
        '/api/heatmap-import/upload',
        formData,
        {
          // withCredentials är redan satt i api-client.ts
          // Men vi säkerställer att det är med här också
          withCredentials: true,
        }
      );

      console.log('✅ HeatmapImport: Upload successful', response.data);

      if (!response.data.success) {
        throw new Error('Failed to upload CSV');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ HeatmapImport: Upload failed', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
        },
      });
      
      // Om det är en 401, logga mer info
      if (error.response?.status === 401) {
        console.error('❌ HeatmapImport: 401 Unauthorized - Authentication failed', {
          hasAccessToken: !!Cookies.get('accessToken'),
          hasRefreshToken: !!Cookies.get('refreshToken'),
          cookies: document.cookie,
        });
      }
      
      throw error;
    }
  }

  /**
   * Hämta metadata för en session
   */
  async getMetadata(sessionId: string): Promise<ImportMetadata> {
    const response = await api.get<{ success: boolean; data: ImportMetadata }>(
      `/api/heatmap-import/${sessionId}/metadata`
    );

    if (!response.data.success) {
      throw new Error('Failed to get metadata');
    }

    return response.data.data;
  }

  /**
   * Hämta lista med registreringsnummer för en session
   */
  async getVehicles(sessionId: string): Promise<string[]> {
    const response = await api.get<{ success: boolean; data: string[] }>(
      `/api/heatmap-import/${sessionId}/vehicles`
    );

    if (!response.data.success) {
      throw new Error('Failed to get vehicles');
    }

    return response.data.data;
  }

  /**
   * Hämta punkter för heatmap (samma format som vanlig heatmap)
   */
  async getPoints(
    sessionId: string,
    params: {
      vehicleIds?: string;
      from?: string;
      to?: string;
      mode?: string;
    }
  ): Promise<{ points: Array<{ lat: number; lng: number; intensity: number; count?: number }>; tripCount: number }> {
    const queryParams = new URLSearchParams();
    
    if (params.vehicleIds) {
      queryParams.append('vehicleIds', params.vehicleIds);
    }
    if (params.from) {
      queryParams.append('from', params.from);
    }
    if (params.to) {
      queryParams.append('to', params.to);
    }
    if (params.mode) {
      queryParams.append('mode', params.mode);
    }

    const response = await api.get<{ success: boolean; data: { points: any[]; tripCount: number } }>(
      `/api/heatmap-import/${sessionId}/points?${queryParams.toString()}`
    );

    if (!response.data.success) {
      throw new Error('Failed to get points');
    }

    return response.data.data;
  }

  /**
   * Rensa session-data
   */
  async clearSession(sessionId: string): Promise<void> {
    await api.delete(`/api/heatmap-import/${sessionId}`);
  }
}

export const heatmapImportService = new HeatmapImportService();
export default heatmapImportService;

